import { supabase } from '../config/supabase';

/**
 * Table and storage bucket name constants for Supabase
 */
export const TABLES = Object.freeze({
  USER_PROFILES: 'user_profiles',
  CHAT_MESSAGES: 'chat_messages',
});
export const BUCKETS = Object.freeze({
  DEFAULT: 'default', // Change as needed
});

// -------------------- USER PROFILE HELPERS --------------------

/**
 * Update a user profile in Supabase
 * @param {string} userId - The user_id to update
 * @param {object} updates - The fields to update
 * @returns {Promise<object>} Updated profile data
 * @throws {Error} If update fails
 */
export const updateUserProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from(TABLES.USER_PROFILES)
    .update(updates)
    .eq('user_id', userId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

/**
 * Fetch a user profile by userId
 * @param {string} userId
 * @returns {Promise<object>} User profile data
 * @throws {Error} If fetch fails
 */
export const getUserById = async (userId) => {
  const { data, error } = await supabase
    .from(TABLES.USER_PROFILES)
    .select('*')
    .eq('user_id', userId)
    .single();
  if (error) throw error;
  return data;
};

// -------------------- CHAT HELPERS --------------------

/**
 * Save a chat message to Supabase
 * @param {object} messageData - The message object to save
 * @returns {Promise<object>} The saved message
 * @throws {Error} If insert fails
 */
export const saveChatMessage = async (messageData) => {
  const { data, error } = await supabase.from(TABLES.CHAT_MESSAGES).insert([messageData]).select();
  if (error) throw error;
  return data[0];
};

/**
 * Get chat history for a user
 * @param {string} userId
 * @param {number} [limit=50] - Max messages to fetch
 * @returns {Promise<object[]>} Array of chat messages
 * @throws {Error} If fetch fails
 */
export const getChatHistory = async (userId, limit = 50) => {
  const { data, error } = await supabase
    .from(TABLES.CHAT_MESSAGES)
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
};

// -------------------- FILE STORAGE HELPERS --------------------

/**
 * Upload a file to Supabase storage
 * @param {string} bucketName - Storage bucket name
 * @param {string} filePath - Path in the bucket
 * @param {File|Blob} file - File to upload
 * @returns {Promise<object>} Upload result
 * @throws {Error} If upload fails
 */
export const uploadFile = async (bucketName, filePath, file) => {
  const { data, error } = await supabase.storage.from(bucketName).upload(filePath, file);
  if (error) throw error;
  return data;
};

/**
 * Get a public URL for a file in Supabase storage
 * @param {string} bucketName
 * @param {string} filePath
 * @returns {string} Public URL
 */
export const getFileUrl = (bucketName, filePath) => {
  const { data } = supabase.storage.from(bucketName).getPublicUrl(filePath);
  return data.publicUrl;
};

// -------------------- REALTIME SUBSCRIPTION HELPERS --------------------

/**
 * Subscribe to realtime changes for a Supabase table
 * @param {string} tableName - Table to subscribe to
 * @param {object} filter - Additional filter options (e.g., {filter: ...})
 * @param {function} callback - Handler for payload
 * @returns {function} Unsubscribe function
 */
export const subscribeToTable = (tableName, filter, callback) => {
  const subscription = supabase
    .channel('table-db-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: tableName,
        ...filter,
      },
      (payload) => callback(payload),
    )
    .subscribe();
  return () => {
    supabase.removeChannel(subscription);
  };
};
